/* eslint-disable no-restricted-imports */
import * as React from 'react';

import { Heading, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';

// Tailwind's default 4px-grid scale. We explicitly list the slots we use
// in the product so the preview reflects real spacing, not every rung
// that theoretically exists.
const SCALE = [
    { name: '0', value: '0', rem: '0' },
    { name: '0.5', value: '2px', rem: '0.125rem' },
    { name: '1', value: '4px', rem: '0.25rem' },
    { name: '1.5', value: '6px', rem: '0.375rem' },
    { name: '2', value: '8px', rem: '0.5rem' },
    { name: '3', value: '12px', rem: '0.75rem' },
    { name: '4', value: '16px', rem: '1rem' },
    { name: '5', value: '20px', rem: '1.25rem' },
    { name: '6', value: '24px', rem: '1.5rem' },
    { name: '8', value: '32px', rem: '2rem' },
    { name: '10', value: '40px', rem: '2.5rem' },
    { name: '12', value: '48px', rem: '3rem' },
    { name: '16', value: '64px', rem: '4rem' },
    { name: '20', value: '80px', rem: '5rem' },
    { name: '24', value: '96px', rem: '6rem' }
];

// Custom tokens declared in tailwind.config.js. Listed by category so the
// page documents what each named size is actually used for — this is the
// reference developers reach for when tempted to drop an arbitrary value.
const CUSTOM_TOKENS = [
    {
        group: 'Layout widths',
        items: [
            { name: 'left-menu', value: '225px', utility: 'tw-w-left-menu', description: 'App left navigation column.' },
            { name: '35pct / 65pct', value: '35% / 65%', utility: 'tw-w-35pct / tw-w-65pct', description: 'Split layouts (Alarms, Schedule).' },
            { name: 'widget-preview', value: '320px', utility: 'tw-w-widget-preview', description: 'Dashboard widget preview canvas.' },
            { name: 'catalog-aside', value: '320px', utility: 'lg:tw-w-catalog-aside', description: 'Design-system nav sidebar.' },
            { name: 'calendar-popover', value: '640px', utility: 'tw-w-calendar-popover', description: 'IntervalPicker popover (two calendars).' }
        ]
    },
    {
        group: 'Layout max widths',
        items: [
            { name: 'bubble', value: '75%', utility: 'tw-max-w-bubble', description: 'Chat message bubble.' },
            { name: 'modal', value: '80vw', utility: 'tw-max-w-modal', description: 'Wizard dialog, signup banners.' },
            { name: 'tooltip', value: '240px', utility: 'tw-max-w-tooltip', description: 'Tooltip floating surface.' },
            { name: 'toast', value: '420px', utility: 'tw-max-w-toast', description: 'Toast viewport (md+).' },
            { name: 'catalog-page', value: '1600px', utility: 'tw-max-w-catalog-page', description: 'Design-system page max width.' }
        ]
    },
    {
        group: 'Layout heights',
        items: [
            { name: 'modal-band', value: '15vh', utility: 'tw-h-modal-band', description: 'Modal top/bottom spacer band.' },
            { name: 'auth-form-area', value: '255px', utility: 'tw-h-auth-form-area', description: 'Login form swap container.' },
            { name: 'widget-preview', value: '200px', utility: 'tw-h-widget-preview', description: 'Dashboard widget preview.' },
            { name: 'catalog-preview-sm/md/lg', value: '180 / 240 / 360 px', utility: 'tw-h-catalog-preview-*', description: 'Story preview boxes.' }
        ]
    },
    {
        group: 'Layout min widths / heights',
        items: [
            { name: 'modal (min-w / min-h)', value: '80vw / 70vh', utility: 'tw-min-w-modal / tw-min-h-modal', description: 'Wizard dialog consistency.' },
            { name: 'sidebar-menu', value: '220px', utility: 'tw-min-w-sidebar-menu', description: 'SidebarMenu basis floor.' },
            { name: 'catalog-nav', value: '280px', utility: 'tw-min-w-catalog-nav', description: 'NavigationMenu preview.' },
            { name: 'screen-dvh', value: '100dvh', utility: 'tw-min-h-screen-dvh', description: 'Login fullscreen (mobile-safe).' },
            { name: 'content', value: 'calc(100vh - 50px)', utility: 'tw-min-h-content', description: 'Below the 50px topbar.' }
        ]
    },
    {
        group: 'Semantic z-index',
        items: [
            { name: 'lift', value: '1', utility: 'tw-z-lift', description: 'Lift sibling (focus ring, badge).' },
            { name: 'modal', value: '40', utility: 'tw-z-modal', description: 'Modal / dialog backdrop + container.' },
            { name: 'overlay', value: '50', utility: 'tw-z-overlay', description: 'Popover, tooltip, dropdown, menu.' },
            { name: 'notification', value: '100', utility: 'tw-z-notification', description: 'Toast, global banner.' }
        ]
    }
];

const ScaleRow = ({ entry }) => (
    <div className="tw-grid tw-grid-cols-[3rem_5rem_5rem_1fr] tw-items-center tw-gap-4 tw-border-b tw-border-border tw-py-2 last:tw-border-b-0">
        <Text variant="control">{entry.name}</Text>
        <Text tone="muted" variant="caption">{entry.value}</Text>
        <Text tone="muted" variant="caption">{entry.rem}</Text>
        <div
            className="tw-h-3 tw-rounded-sm tw-bg-primary"
            style={{ width: entry.value }}
        />
    </div>
);

const TokenRow = ({ item }) => (
    <div className="tw-grid tw-grid-cols-1 tw-gap-1 tw-border-b tw-border-border tw-py-3 last:tw-border-b-0 md:tw-grid-cols-[12rem_10rem_1fr] md:tw-gap-6">
        <Text variant="control">{item.name}</Text>
        <Text tone="muted" variant="caption"><code>{item.value}</code></Text>
        <div className="tw-space-y-1">
            <Text as="p" tone="muted" variant="caption"><code>{item.utility}</code></Text>
            <Text as="p" tone="muted" variant="body-sm">{item.description}</Text>
        </div>
    </div>
);

export default function SpacingFoundation() {
    return (
        <div className="tw-space-y-8">
            <header className="tw-space-y-2">
                <Text tone="muted" variant="overline">Foundation</Text>
                <Heading as="h2" variant="title-lg">Spacing</Heading>
                <Text as="p" tone="muted" variant="body">
                    The default Tailwind 4px scale covers the vast majority of paddings,
                    margins and gaps. Custom tokens exist for layout-sized dimensions that
                    recur across the product — always prefer them over arbitrary values.
                </Text>
            </header>

            <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                <Heading as="h3" variant="title">Base scale</Heading>
                <Text tone="muted" variant="body-sm">
                    Use for padding, margin, gap, and any utility that accepts a length.
                </Text>
                <div className="tw-mt-4 tw-grid tw-grid-cols-[3rem_5rem_5rem_1fr] tw-gap-4 tw-pb-2">
                    <Text tone="muted" variant="overline">Scale</Text>
                    <Text tone="muted" variant="overline">Px</Text>
                    <Text tone="muted" variant="overline">Rem</Text>
                    <Text tone="muted" variant="overline">Preview</Text>
                </div>
                {SCALE.map(entry => (
                    <ScaleRow key={entry.name} entry={entry} />
                ))}
            </section>

            {CUSTOM_TOKENS.map(section => (
                <section key={section.group} className={`${WM_FLAT_SURFACE_CLASSNAME} tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
                    <Heading as="h3" variant="title">{section.group}</Heading>
                    <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-1 tw-pb-2 md:tw-grid-cols-[12rem_10rem_1fr] md:tw-gap-6">
                        <Text tone="muted" variant="overline">Token</Text>
                        <Text tone="muted" variant="overline">Value</Text>
                        <Text tone="muted" variant="overline">Utility / usage</Text>
                    </div>
                    {section.items.map(item => (
                        <TokenRow key={item.name} item={item} />
                    ))}
                </section>
            ))}
        </div>
    );
}
